/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  Spinner,
  TabbedView,
  CashLoanProductCard,
  RetailLoanProductCard,
  ConsolidationLoanProductCard,
  ProductPayments,
  ObjectCases,
  ObjectFiles,
  NavLink,
  CenteredContainerDisplay,
  SvgIcon,
  FileUploadProgressModal,
  FileSelectUpload
} from '../'

import {
  decodeBase64,
  getNodeTypeFromBase64Id
} from '../../utils'

import {
  FILETYPE_LOAN_PROOF_OF_PAYMENT
} from '../../constants'

const HISTORY_TAB = 0
const CASES_TAB = 1
const FILES_TAB = 2

export class CreditProductDetail extends React.PureComponent {
  static propTypes = {
    loan: PropTypes.object.isRequired,
    openTab: PropTypes.oneOf([ HISTORY_TAB, CASES_TAB, FILES_TAB ]).isRequired,  // see defined constants above class definition for values
    onTabClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    openTab: HISTORY_TAB
  }

  constructor(props) {
    super(props)

    this.state = {
      numberOfCases: 0,
      numberOfFiles: 0,
      fileUploading: false,
      fileUploadSuccess: null,
      fileUploadFail: false
    }

    this.productDetailSectionTabRef = React.createRef()
  }

  componentDidMount() {
    if (this.props.openTab !== HISTORY_TAB) {
      this.productDetailSectionTabRef.current.focusOnActiveTab()
    }
  }

  getRepayingFrequency = (payrollCycle) => (
    {
      'WEEKLY': '/ week',
      'FORTNIGHTLY': '/ fortnight',
      'MONTHLY': '/ month'
    }[payrollCycle]
  )

  displayLoanProductCard = () => {
    let repayingFrequency = this.getRepayingFrequency(this.props.payrollCycle)
    let { id, loanType, lmsStatus, amount, repaid, instalmentCurrent, balance, repayableCurrent,
      finalInstalmentAt, refreshedAt, lmsId, loanapplication, issuedBy, activatedBy,
      workflowrunProcessing } = this.props.loan

    return (
      loanType === 'CASH' || loanType === 'EMERGENCY'
        ? <CashLoanProductCard
            loanId={id}
            loanStatus={lmsStatus}
            loanAmount={amount}
            repaid={repaid}
            repaying={instalmentCurrent}
            balance={balance}
            repayable={repayableCurrent}
            lastPayment={finalInstalmentAt
              ? moment(finalInstalmentAt).format("D MMM 'YY")
              : "Unknown"
            }
            payrollCycle={repayingFrequency}
            refreshedAt={refreshedAt
              ? moment(refreshedAt).format("D MMM 'YY")
              : "Unknown"
            }
            className='slds-size_1-of-2'
            detailView={true}
            mycoMaxId={lmsId}
            sourceApplication={loanapplication
              ? <NavLink to={`/os/products/credit/applications/${loanapplication.id}`}>
                  {`Last updated ${moment(loanapplication.updatedAt).format("D MMM 'YY")}`}
                </NavLink>
              : 'Unknown'
            }
            issuedBy={issuedBy
              ? `${issuedBy.firstName} ${issuedBy.lastName}`
              : 'Unknown'
            }
            activatedBy={activatedBy
              ? `${activatedBy.firstName} ${activatedBy.lastName}`
              : 'Unknown'
            }
            disbursalWorkflow={workflowrunProcessing}
            isCredit={getNodeTypeFromBase64Id(id) === 'CreditNode'}
            emergencyLoan={loanType === 'EMERGENCY'}
          />
        : loanType === 'CONSOLIDATION'
          ? <ConsolidationLoanProductCard
              loanId={id}
              loanStatus={lmsStatus}
              loanAmount={amount}
              repaid={repaid}
              repaying={instalmentCurrent}
              balance={balance}
              repayable={repayableCurrent}
              lastPayment={finalInstalmentAt
                ? moment(finalInstalmentAt).format("D MMM 'YY")
                : "Unknown"
              }
              payrollCycle={repayingFrequency}
              refreshedAt={moment(refreshedAt).format("D MMM 'YY")}
              className='slds-size_1-of-2'
              detailView={true}
              mycoMaxId={lmsId}
              sourceApplication={loanapplication
                ? <NavLink
                    to={`/os/products/credit/applications/${loanapplication.id}`}>
                    {`Last updated ${moment(loanapplication.updatedAt).format("D MMM 'YY")}`}
                  </NavLink>
                : 'Unknown'
              }
              issuedBy={issuedBy
                ? `${issuedBy.firstName} ${issuedBy.lastName}`
                : 'Unknown'
              }
              activatedBy={activatedBy
                ? `${activatedBy.firstName} ${activatedBy.lastName}`
                : 'Unknown'
              }
              disbursalWorkflow={workflowrunProcessing}
              isCredit={getNodeTypeFromBase64Id(id) === 'CreditNode'}
            />
          : <RetailLoanProductCard
              loanId={id}
              loanStatus={lmsStatus}
              loanAmount={amount}
              repaid={repaid}
              repaying={instalmentCurrent}
              balance={balance}
              repayable={repayableCurrent}
              lastPayment={finalInstalmentAt
                ? moment(finalInstalmentAt).format("D MMM 'YY")
                : "Unknown"
              }
              payrollCycle={repayingFrequency}
              refreshedAt={moment(refreshedAt).format("D MMM 'YY")}
              className='slds-size_1-of-2'
              detailView={true}
              mycoMaxId={lmsId}
              sourceApplication={loanapplication
                ? <NavLink
                    to={`/os/products/credit/applications/${loanapplication.id}`}
                    className={this.getIconColour(loanType) || 'success'}>
                    {`Last updated ${moment(loanapplication.updatedAt).format("D MMM 'YY")}`}
                  </NavLink>
                : 'Unknown'
              }
              issuedBy={issuedBy
                ? `${issuedBy.firstName} ${issuedBy.lastName}`
                : 'Unknown'
              }
              activatedBy={activatedBy
                ? `${activatedBy.firstName} ${activatedBy.lastName}`
                : 'Unknown'
              }
              disbursalWorkflow={workflowrunProcessing}
              isCredit={getNodeTypeFromBase64Id(id) === 'CreditNode'}
            />
    )
  }

  displayActions = (isCredit) => {
    return (
      <CenteredContainerDisplay
        content={
          <div className='slds-size_3-of-4 slds-m-left_x-large'>
            <div className='u-font-size-larger slds-m-bottom_medium'>
              <b>Files</b>
            </div>
            <FileSelectUpload
              uploadButton={
                <div className='slds-grid slds-size_1-of-1 slds-border_bottom slds-p-bottom_x-small clickable'>
                  <div>
                    Add proof of payment
                  </div>
                  <div className='slds-col_bump-left'>
                    {this.state.fileUploading
                      ? <Spinner className='slds-is-relative' size='small' />
                      : <SvgIcon
                          variant='picsaos'
                          symbol='upload'
                          size='small'
                          colour={this.getIconColour(this.props.loan.loanType) || 'success'}
                        />
                    }
                  </div>
                </div>
              }
              useFullWidth={true}
              onFileSelected={e => {
                this.setState({ fileUploading: true })
                this.uploadFile(e.target.files[0], isCredit)
              }}
            />
          </div>
        }
        yAdjustPercentage={-35}
        useFullWidth={true}
        className='slds-size_1-of-2'
      />
    )
  }

  getIconColour = (loanType) => (
    {
      'CASH': 'cash_loan',
      'CONSOLIDATION': 'consolidation_loan',
      'RETAIL_CASH': 'retail_loan'
    }[loanType]
  )

  uploadFile = (file, isCredit) => {
    let reader = new FileReader()
    let headers = new Headers({
      "Authorization": "Token " + localStorage.getItem('token') || null
    })

    let data = new FormData()
    data.append('file', file)
    data.append('filetype', FILETYPE_LOAN_PROOF_OF_PAYMENT)
    data.append('relationships', JSON.stringify([
      ['loan', decodeBase64(this.props.loan.id)],
      ['identity', decodeBase64(this.props.loan.client.identity.id)]
    ]))

    reader.onload = (e) => {
      fetch(process.env.REACT_APP_API_ROOT + "/v1/file/", {
        method: 'post',
        headers: headers,
        body: data
      })
      .then((data) => {
        console.log('File upload response code', data)
        if (data.ok) {  // covers 200-range of http responses
          console.log('File upload success')
          this.setState({ uploadFileSuccess: true })
        } else {
          console.log('File upload fail')
          this.setState({ uploadFileSuccess: false })
        }
      })
      .catch((error) => {
        console.log('File upload request failed', error)
        this.setState({ uploadFileSuccess: false })
      })
    }

    reader.readAsText(file)
  }

  render() {
    if (this.state.fileUploading) {
      return (
        <FileUploadProgressModal
          uploadSuccess={this.state.uploadFileSuccess}
          onUploadSuccessClick={() => this.setState({
            fileUploading: false,
            uploadFileSuccess: null
          })}
          onUploadFailClick={() => this.setState({
            fileUploading: false,
            uploadFileSuccess: null
          })}
        />
      )

    }

    return (
      <div className={`slds-size_1-of-1 ${this.props.className ? this.props.className : ''}`}>
        {/* product overview section */}
        <div className='slds-p-around_large slds-border_bottom'>
          <div className='slds-grid'>
            {this.displayLoanProductCard()}
            {this.displayActions(this.props.isCredit)}
          </div>
        </div>
        {/* product detail section */}
        <TabbedView
          ref={this.productDetailSectionTabRef}
          tabs={[
            'History',
            `Cases for this product${this.state.numberOfCases > 0 ? ` (${this.state.numberOfCases})` : ''}`,
            `Files${this.state.numberOfCases > 0 ? ` (${this.state.numberOfFiles})` : ''}`,
          ]}
          contents={[
            <ProductPayments
              product={this.props.loan.id}
              contentTypeModel={this.props.isCredit ? 'credit' : 'loan'} />,
            <ObjectCases object={this.props.loan.id} pageBasedPagination={true} />,
            <ObjectFiles
              object={this.props.loan.id}
              noRecordsToDisplay={
                <div className={this.props.className}>
                  <SvgIcon
                    variant='utility'
                    symbol='info'
                    colour='light'
                    size='small'
                    className='slds-m-right_x-small'
                  />
                  No related items found
                </div>
              }
            />
          ]}
          tabSpacing='x-large'
          selectedTab={this.props.openTab}
          onClick={this.props.onTabClick}
          tabsClassName='slds-p-top_small slds-p-left_large slds-p-right_large'
        />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/credit-product-detail/index.js